import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Account, Auth } from "@air/api";
import { WORKSPACE_INTEGRATION_SSO_NOT_ENABLED } from "@air/errors";
import { FormikField } from "@air/formik-field";
import { Button } from "@air/primitive-button";
import { Input } from "@air/primitive-input";
import { callOnFullAccountUserPool } from "@air/utils-auth";
import { reportErrorToBugsnag } from "@air/utils-error";
import { useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useRouter } from "next/router";
import React, { memo, useCallback } from "react";
import * as Yup from "yup";
import Form from "~/components/Form";
import { Routes } from "~/constants/routes";
import { CONTINUE_WITH_PASSWORD, LOGIN_EMAIL_INPUT, LOGIN_SUBMIT_BUTTON, SAML_SSO_FORM } from "~/constants/testIDs";
import { getAccountsSSOOptionsKey } from "~/hooks/useGetAccountsSSOOptions";
import { isAirrorInstance } from "~/swr-hooks/isAirrorInstance";
import { sanitizeEmail } from "~/utils/EmailUtils";
import { getProviderNameFromSSOOptions } from "~/utils/SSOUtils";
const SSOLoginSchema = Yup.object().shape({
    email: Yup.string().email("Please enter a valid email address.").required("Please enter a valid email address.").default("")
}).required();
export const SamlSSOLoginForm = /*#__PURE__*/ memo((param)=>{
    let { onContinueWithPasswordClick, onRedirect, customState } = param;
    const { maintenanceModeJson } = useFlags();
    const isFormDisabled = !!(maintenanceModeJson === null || maintenanceModeJson === void 0 ? void 0 : maintenanceModeJson.maintenanceMessage);
    const { push } = useRouter();
    const queryClient = useQueryClient();
    const { selectWorkspaceAuth } = useFlags();
    const onSubmit = useCallback(async (values, param)=>{
        let { setSubmitting, setErrors } = param;
        return callOnFullAccountUserPool(async ()=>{
            const userEmail = sanitizeEmail(values.email);
            try {
                setSubmitting(true);
                const result = await Account.getAccountSSOOptionsV2({
                    email: userEmail
                });
                if (isAirrorInstance(result) || result.data.length === 0) {
                    queryClient.setQueryData(getAccountsSSOOptionsKey(userEmail), isAirrorInstance(result) ? [] : result.data);
                    setSubmitting(false);
                    setErrors({
                        email: WORKSPACE_INTEGRATION_SSO_NOT_ENABLED.message
                    });
                } else {
                    queryClient.setQueryData(getAccountsSSOOptionsKey(userEmail), result.data);
                    const userEmailDomain = userEmail.split("@")[1];
                    const domains = selectWorkspaceAuth === null || selectWorkspaceAuth === void 0 ? void 0 : selectWorkspaceAuth.domains;
                    if ((domains === null || domains === void 0 ? void 0 : domains.includes(userEmailDomain)) && result.data.length > 1) {
                        push({
                            pathname: Routes.selectSSOProviderToLoginTo,
                            query: {
                                email: userEmail,
                                customState: customState ? JSON.stringify(customState) : undefined
                            }
                        });
                    } else {
                        const ssoOption = result.data[0];
                        try {
                            const samlProvider = getProviderNameFromSSOOptions(ssoOption);
                            if (samlProvider) {
                                Auth.ssoSamlSignin(samlProvider, {
                                    customState
                                });
                            } else {
                                throw new Error("Missing samlProvider!");
                            }
                            onRedirect === null || onRedirect === void 0 ? void 0 : onRedirect();
                        } catch (error) {
                            reportErrorToBugsnag({
                                error,
                                context: "Failed login user using saml sso",
                                metadata: {
                                    ssoOption
                                }
                            });
                            setErrors({
                                email: "An error occurred. Please try again later"
                            });
                        }
                    }
                }
            } catch (error) {
                setSubmitting(false);
                reportErrorToBugsnag({
                    error,
                    context: "Failed get account SSO options",
                    metadata: {
                        updatedAccount: {
                            email: userEmail
                        }
                    }
                });
                setErrors({
                    email: "An error occurred. Please try again later"
                });
            }
        });
    }, [
        customState,
        onRedirect,
        push,
        queryClient,
        selectWorkspaceAuth === null || selectWorkspaceAuth === void 0 ? void 0 : selectWorkspaceAuth.domains
    ]);
    return /*#__PURE__*/ _jsx(Formik, {
        initialValues: SSOLoginSchema.cast({}),
        validationSchema: SSOLoginSchema,
        onSubmit: onSubmit,
        children: (param)=>{
            let { isSubmitting } = param;
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Form, {
                        "data-testid": SAML_SSO_FORM,
                        children: [
                            /*#__PURE__*/ _jsx(FormikField, {
                                className: "mb-8",
                                component: /*#__PURE__*/ _jsx(Input, {
                                    placeholder: "Add your email",
                                    type: "email",
                                    autoComplete: "username",
                                    "data-testid": LOGIN_EMAIL_INPUT,
                                    disabled: isFormDisabled,
                                    size: "large"
                                }),
                                label: "Work email",
                                name: "email",
                                id: "email"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "submit",
                                size: "large",
                                className: "w-full",
                                isLoading: isSubmitting,
                                "data-testid": LOGIN_SUBMIT_BUTTON,
                                disabled: isFormDisabled,
                                children: "Log in"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-5 flex w-full justify-center",
                        children: /*#__PURE__*/ _jsx("button", {
                            "data-testid": CONTINUE_WITH_PASSWORD,
                            className: "text-14 font-medium text-grey-10 underline hover:text-grey-12",
                            onClick: onContinueWithPasswordClick,
                            children: "Continue with Google or password"
                        })
                    })
                ]
            });
        }
    });
});
SamlSSOLoginForm.displayName = "SamlSSOLoginForm";
